import { Map, StringMap } from "schemas/functions";

import { AddressComponent } from "components/LocationAutocomplete/schema";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import { SelectOption } from "features/Influencer/Tracking/schema";

import { EmailAddress, EmailDraft, EmailSettings, Message } from "./email";
import { OverridePlanName, Plans } from "./payments";

export type Company = {
  id?: number;
  name: string;
};

export enum ContactMethod {
  EMAIL = "email",
  INSTAGRAM = "instagram",
}

export type Contact = {
  id?: number;
  companyId?: number;
  ownerUserId?: number;
  name?: string;
  title?: string;
  email?: string;
  contactMethod: ContactMethod;
  priority?: ContactPriority | null;
  website?: string;
  tags?: string;
  notes?: string;
  company?: Company;
  interactions?: Interaction[];
  createdAt?: number;
  opportunities?: Opportunity[];
  latestMessageStatus?: string;
};

export enum OrganizationType {
  FOUNDER = "founder",
  INFLUENCER = "influencer",
}

export type OrganizationUser = {
  id: number;
  organizationId: number;
  email: string;
  name: string;
  profileImageUrl?: string;
  lastViewedOutreachAt?: number;
  doNotEmail?: boolean;
};

export enum SubscriptionType {
  TRIALING = "trialing",
  ACTIVE = "active",
  INCOMPLETE = "incomplete",
  INCOMPLETE_EXPIRED = "incomplete_expired",
  PAST_DUE = "past_due",
  CANCELED = "canceled",
  UNPAID = "unpaid",
  PAUSED = "paused",
}

export enum SubscriptionPortalFlow {
  subscription_cancel = "subscription_cancel",
}

export type Subscription = {
  createdAt: number;
  isSubscribed: boolean;
  billingCycle: [number, number];
  creditsCycle: [number, number];
  monthlyLimit: number;
  remainingMessages: number;
  remainingChatMessages: number;
  chatMonthlyLimit: number;
  campaignLimit: number;
  remainingBrandRequests: number;
  brandRequestsMonthlyLimit: number;
  overridePlanName: OverridePlanName;
  referralsBoost: number;
  partnershipData?: AddOnItem;
  customEmailData?: AddOnItem;
  ouiStudioData?: AddOnItem;
  strategyPackageData?: AddOnItem;
  subscription: StripeSubscription;
  repitchingData?: AddOnItem;
  bannerPromoCode?: BannerPromoCode;
  subscriptionStartDate?: number;
  emailVolumes?: AddOnItem[];
};

export enum BannerCouponName {
  black_friday = "Black Friday Sale - 25% Off",
  new_subscriber = "New Subscriber - 25% Off",
  money_back_guarantee = "Money Back Guarantee",
  end_of_year_sale = "New Year Sale - 30% Off",
}

export type BannerPromoCode = {
  dateEnding: string;
  couponName: BannerCouponName;
  discountPercent: number;
  checkoutCode?: string;
  applicablePlans?: Plans[];
};

export type StripeSubscription = {
  id?: number;
  stripeCustomerId?: string;
  status: SubscriptionType;
  planName?: Plans;
  cancelAt?: number;
  stripeSubscriptionId?: string;
  discountedPrice?: number;
  createdAt?: number;
  startDate?: number;
  totalMonthsSince?: number;
};

export type AddOnDetails = {
  display_name: string;
  max_quantity: number;
  price: number;
  description: string;
  recurring: boolean;
  can_purchase_multiple: boolean;
  product?: string;
};

export enum UpgradeDialogSource {
  all_tools = "All Tools",
  access_partnership_data = "Access Partnership Data",
  black_friday = "Black Friday - Home Page",
  campaigns_limit = "Campaign Limit",
  change_plan = "Change Plan",
  countdown_banner = "Countdown Banner",
  exclusive_contact = "Exclusive Contact",
  exclusive_brand = "Exclusive Brand",
  end_of_year_sale_home_page = "End Of Year Sale - Home Page",
  end_of_year_sale_countdown_banner = "End Of Year Sale - Countdown Banner",
  email_tracking = "Email Tracking",
  email_volume_dialog_source = "Email Volume Dialog Source",
  email_volume_from_va_dashboard = "Email Volume From VA Dashboard",
  email_volume_from_ps_email = "Email Volume from P.S Email",
  free_template_from_templates_page = "Free Template from Templates Page",
  free_template_from_home_page = "Free Template from Home Page",
  free_template_coupon_email = "Free Template Coupon Email",
  free_template_with_annual_plan = "Free Template with Annual Plan",
  hits_limit = "Hits Limit",
  hits_bulk_send_limit = "Hits Bulk Send Limit",
  hits_brand_request_limit = "Hits Brand Request Limit",
  hits_campaign_limit = "Hits Campaign Limit",
  hits_personalized_ai_messages_limit = "Hits Personalized AI Messages Limit",
  hits_personalized_ai_messages_limit_from_refresh = "Hits Personalized AI Messages Limit (From Refresh)",
  instagram_connected_dialog = "Instagram Connected Dialog",
  instagram_onboarding_paywall_pro = "Instagram Onboarding Paywall - Pro",
  link_query = "Link Query",
  navigation_bar = "Navigation Bar",
  onboarding_paywall = "Onboarding Paywall",
  reply_score = "Reply Score",
  splash_page_annual_template = "Splash Page Annual Template",
  restricted_filtering_feature = "Restricted Filtering Feature",
  top_bar_emails_sent_progress = "Top Bar Emails Sent Progress",
  targeted_check_ins = "Targeted Check-ins",
  upgrade_va_button = "Upgrade VA Button",
  upgrade_offer_dialog = "Upgrade Offer Dialog",
  va_metrics_sidebar = "VA Metrics Sidebar",
  va_preview = "VA Preview",
  mobile_app_navigation_bar = "Mobile App Navigation Bar",
  mobile_app_settings = "Mobile App Settings",
}

export enum VaTemplateStatus {
  pending = "pending",
  approved = "approved",
}

export type Organization = {
  id: number;
  name: string;
  type: OrganizationType;
  vaStatus: VAStatus;
  overridePlanName?: OverridePlanName;
  bentoCategories?: BentoCategory[];
  excludedBentoSearchFilterIds?: number[];
  includedBentoSearchFilterIds?: number[];
  createdAt?: number;
  overrideEmailQuota?: number;

  allowShareContacts?: boolean;
  allowImportEmail?: boolean;
  lastGeneratedCheckInsAt?: number;
  lastCompletedCheckInsAt?: number;

  isBrand?: boolean;
  isVaUser?: boolean;
  isPaidUser?: boolean;
};

export enum VAStatus {
  pending = "pending",
  approved = "approved",
  disqualified = "disqualified",
  not_interested = "not_interested",
}

export enum PreferredBrandSize {
  small = "small",
  large = "large",
}

export type BentoCategory = {
  displayName?: string;
  name: string;
  id: number;
  subcategories?: BentoCategory[];
  doNotDisplay?: boolean;
};

export type OrganizationBentoCategory = {
  organizationId: number;
  bentoCategoryId: number;
};

export enum AddOnItemName {
  partnership_data = "partnership_data",
  support_call = "support_call",
  pitching_templates = "pitching_templates",
  niche = "niche",
  custom_email = "custom_email",
  oui_studio = "oui_studio",
  repitching_tier_one = "repitching_tier_one",
  repitching_tier_two = "repitching_tier_two",
  repitching_tier_three = "repitching_tier_three",
  strategy_package = "strategy_package",
  email_volume_monthly = "email_volume_monthly",
  email_volume_yearly = "email_volume_yearly",
}

export type AddOnItem = {
  createdAt: number;
  id: number;
  itemName: string;
  quantity: number;
  status?: SubscriptionType;
  cancelAt?: number;
  billingCycle?: [number, number];
  domainInfo?: CustomDomainInfo;
  purchaseDetails?: any;
  templateIntakeForms?: TemplateIntakeForm[];
  completed?: boolean;
};

export type OrganizationProfile = {
  // Default
  id: number;
  isUgcCreator?: boolean;
  isInfluencer?: boolean;
  otherType?: string;
  phoneNumber?: string;
  hasOnboarded?: boolean;
  reviewedFirstTemplate?: boolean;

  // Location
  city?: string;
  location?: string; // country code
  latitude?: number;
  longitude?: number;
  addressComponents?: AddressComponent[];

  // Onboarding Page 1
  instagramUrl?: string;
  tiktokUrl?: string;
  youtubeUrl?: string;
  twitchUrl?: string;
  portfolioUrl?: string;
  primarySocialMediaPlatform?: string;

  // Onboarding Page 3 (ignore preferredBrandSize)
  brands?: string;
  preferredBrandSize?: PreferredBrandSize;
  wishlistBrands?: string[];

  // Onboarding Page 4 (va)
  monthlyIncome?: number[];

  // Onboarding Page 6 (va niche)
  onboardingGoogleDocId?: string;

  // Templates
  introduction?: string;
  signature?: string;
  mostImpressivePartnerships?: string;
  caseStudy?: string;
  numOfFollowers?: number;
  engagement?: string;
  pitchEmail?: string;

  // profile only
  backgroundDetails?: { [key: string]: VaBackground };
};

export enum VaNicheType {
  specific = "specific",
  general = "general",
}

export enum VaBackgroundDetailsField {
  personal_facts = "personal_facts",
  content_type = "content_type",
  content_tone = "content_tone",
  why_brands_like_your_content = "why_brands_like_your_content",
  biggest_brand_names = "biggest_brand_names",
  most_proud_partnerships = "most_proud_partnerships",
  other = "other",
}

export enum VaOrganizationProfileStatus {
  pending = "pending",
  completed = "completed",
}

export enum VaCollabPreference {
  all = "all",
  none = "none",
  some = "some",
}

export type VaBackgroundQuestionAnswer = {
  question: string;
  answer: string;
  field_name: VaBackgroundDetailsField;
  required?: boolean;
};

export type VaBackground = {
  personal_facts?: VaBackgroundQuestionAnswer;
  content_type?: VaBackgroundQuestionAnswer;
  content_tone?: VaBackgroundQuestionAnswer;
  why_brands_like_your_content?: VaBackgroundQuestionAnswer;
  biggest_brand_names?: VaBackgroundQuestionAnswer;
  most_proud_partnerships?: VaBackgroundQuestionAnswer;
  other?: VaBackgroundQuestionAnswer;
};

export type OrganizationVaProfile = {
  id?: number;
  niches?: OrganizationNiche[];
  backgroundDetails?: { [key: string]: VaBackground };
  status: VaOrganizationProfileStatus;
  personalizeOutreach?: boolean;
  openToAffiliates?: VaCollabPreference;
  openToGifted?: VaCollabPreference;
  openToForms?: VaCollabPreference;
  openToSocial?: VaCollabPreference;
};

export enum DiscoverySource {
  word_of_mouth = "word_of_mouth",
  instagram = "instagram",
  tiktok = "tiktok",
  google = "google",
  x = "x",
  community_group_or_course = "community_group_or_course",
  youtube = "youtube",
  other = "other",
}

export const DiscoverySourceDisplayName: Map = {
  [DiscoverySource.word_of_mouth]: "From a friend or colleague",
  [DiscoverySource.instagram]: "Instagram",
  [DiscoverySource.tiktok]: "TikTok",
  [DiscoverySource.google]: "Google",
  [DiscoverySource.x]: "Twitter / X",
  [DiscoverySource.community_group_or_course]: "Community Group/Course",
  [DiscoverySource.youtube]: "YouTube",
  [DiscoverySource.other]: "Other",
};

export enum IncomeLevel {
  starting = "starting",
  gettingFeetWet = "gettingFeetWet",
  gettingHangOfIt = "gettingHangOfIt",
  seasoned = "seasoned",
}

export const IncomeLevelMap: { [key in IncomeLevel]: number[] } = {
  [IncomeLevel.starting]: [0, 0],
  [IncomeLevel.gettingFeetWet]: [0, 500],
  [IncomeLevel.gettingHangOfIt]: [500, 2000],
  [IncomeLevel.seasoned]: [2000, 10000],
};

export enum OnboardAction {
  SKIP_EMAILING_BRANDS = "Skip Emailing Brand",
  DIALOG_CREATED_TEMPLATE = "Dialog Created Template",
  EMAIL_TO_BRAND_SENT = "Email To Brand Sent",
  FINAL_OPT_IN = "Final Opt-In",
  COMPLETE_PAGE = "Complete Page",
}

export type Opportunity = {
  id: string | null;
  company: Company;
  companyId: string | null;
  name: string;
  stage: string;
  fitLevel?: string;
  createdAt?: number;
  updatedAt?: number;
  contacts?: Contact[];
  tags?: OpportunityTag[];
  interactions?: Interaction[];
  contractValueDollars?: number;
  notes?: string;
};

export enum OpportunityStage {
  NEW = "new",
  DISCOVERY = "discovery",
  SCHEDULING_DEMO = "scheduling_demo",
  DEMO = "demo",
  NEGOTIATION = "negotiation",
  WON = "won",
  LOST = "lost",

  OPPORTUNITIES = "opportunities",
  FULFILLING = "fulfilling",
  COMPLETED = "completed",
}

export const OpportunityStageDescription: Map = {
  [OpportunityStage.NEW]: "Opportunities at this stage are not yet categorized",
  [OpportunityStage.DISCOVERY]:
    "Pick the customer's brain on potential usage of the product",
  [OpportunityStage.SCHEDULING_DEMO]:
    "Schedule a demo of the product to the customer(s)",
  [OpportunityStage.DEMO]: "Demo the product to the customer(s)",
  [OpportunityStage.NEGOTIATION]:
    "Negotiate pricing for the product with the customer(s)",
  [OpportunityStage.WON]: "The customer(s) agree to pay for the product",
  [OpportunityStage.LOST]: "The customer(s) are not interested in the product",
};

export const InfluencerStageDescription: Map = {
  [OpportunityStage.OPPORTUNITIES]:
    "Incoming potential brand deals will be listed here.",
  [OpportunityStage.FULFILLING]:
    "In-progress negotiation or creating content with brand",
  [OpportunityStage.COMPLETED]: "Brands with a completed partnership",
};

export enum ContactPriority {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
}

export enum OpportunityFitLevel {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
}

export const FitLevelOrder: string[] = [
  OpportunityFitLevel.LOW,
  OpportunityFitLevel.MEDIUM,
  OpportunityFitLevel.HIGH,
];

export type OpportunityTag = {
  id: number;
  name: string;
};

export enum SearchParams {
  REMINDER_ID = "reminderId",
  CONTACT_ID = "contactId",
  VIEW = "view",
  PAGE = "page",
  DRAWER_OPEN = "drawerOpen",
  DRAWER_TAB = "drawerTab",
  STAGE = "stage",
  COUPON = "coupon",

  // Query Params for Searching Opportunities
  QUERY_TAG_IDS = "queryTagIds",
  QUERY_FIT_LEVEL = "queryFitLevel",
  QUERY_STAGE = "queryStage",
  QUERY_NAME = "queryName",

  // Query params for Searching Contacts
  QUERY_PRIORITY_LEVEL = "queryPriorityLevel",
  QUERY_COMPANY_ID = "queryCompanyId",

  // Query Params for Searching Reminders
  QUERY_TASK_USER = "queryTaskUser",
  QUERY_TASK_STATUS = "queryTaskStatus",
  QUERY_TASK_DUE_DATE = "queryTaskDueDate",

  // Discovery Page
  QUERY_DISCOVER = "queryBrand",
  TAGS_DISCOVER = "tags",
  CATEGORY_ID_DISCOVER = "category_ids",
  TYPE_DISCOVER = "type",
  CONTACT_DISCOVER = "contact",
  SORT_DISCOVER = "sort",
  LOCATION_DISCOVER = "loc",
  GEOPOINTS_DISCOVER = "geopoints",
  FOLLOWING_DISCOVER = "following",
  AGE_DISCOVER = "age",
  GENDER_DISCOVER = "gender",
  COMPENSATION_DISCOVER = "compensation",

  // Referrals
  REFERRAL_CODE = "code",
  AFFILIATE_CODE = "acode",

  // Instagram Onboarding
  OPTION = "option",

  // VA Bundled
  INVOICE_ID = "invoiceId",

  // CONCIERGE PLAN
  CONCIERGE_OPTION = "p",

  // Checkout success page
  PLAN_NAME = "plan",
  UPGRADE_DIALOG_SOURCE = "upgradeDialogSource",
  PAGE_SOURCE = "pageSource",
  CHECKOUT_SOURCE = "checkoutSource",
  SPLASH_PAGE_SOURCE = "splashPageSource",
  FROM_SUBSCRIBE_PAGE = "fromSubscribePage",
}

export type DiscoverCategory = {
  mainCategory: { label: string; key: string };
  subCategories?: { label: string; key: string }[];
};

export const BentoBrandCategoryMap: StringMap = {
  fashion_all: "All Fashion",
  fashion: "Fashion",
  mens_fashion: "Men's Fashion",
  womens_fashion: "Women's Fashion",
  fashion_accessories: "Accessories",
  activewear: "Activewear",
  jewelry: "Jewelry",
  eyewear: "Eyewear",
  shoes: "Shoes",
  bags_watches_other: "Bags, Watches & Other Accessories",
  skincare: "Skincare",
  makeup: "Makeup & Cosmetics",
  haircare: "Haircare",
  beauty: "Beauty",
  fragrance: "Fragrance",
  bath_and_body: "Bath & Body",
  other_beauty_personal_care: "Other Beauty & Personal Care",
  travel: "Travel",
  luggage: "Luggage / Suitcases",
  travel_accessories: "Travel Accessories",
  travel_accommodations: "Travel Tours, Accommodations & Attractions",
  travel_transportation: "Travel Transportation",
  childrens_clothing: "Children's Clothing",
  childrens_food: "Children's Food",
  all_baby_and_maternity: "All Baby & Maternity",
  baby_and_maternity: "Baby & Maternity",
  baby_kids_maternity: "Baby, Kids, & Maternity",
  apps: "Apps",
  technology: "Technology & Electronics",
  games_entertainment: "Games & Entertainment",
  appliances_household_products: "Appliances & Household Products",
  home_decor: "Home Decor",
  pet_products: "Pet Products",
  arts_crafts: "Arts/Crafts",
  sports_fitness_outdoors: "Sports, Fitness & Outdoors",
  health_wellness: "Health/Wellness",
  retailers_ecommerce: "Retailers & E-Commerce Platforms",
  food_beverage_all: "All Food/Beverage",
  restaurants: "Restaurants",
  food_beverage: "Food/Beverage",
  education: "Education",
  business_services: "Business Services",
  life_services: "Life Services",
  financial_services: "Financial Services",
  automotive: "Vehicle & Transportation",
  lifestyle: "Lifestyle",
  other: "Other",
};

export enum Sort {
  Recommended = "recommended",
  RecentlyAdded = "recently-added",
}

export const SORTS = [
  { key: Sort.Recommended, name: "Recommended" },
  { key: Sort.RecentlyAdded, name: "Recently Added" },
];

export enum ContactType {
  All = "all",
  NotContactedThisWeek = "not-contacted-this-week",
  NonGenericEmails = "non-generic-emails",
  HasContacts = "has-contacts",
  NeverPitched = "never-pitched",
}

export enum CompanyType {
  All = "all",
  VerifiedPaidPartnership = "verified-paid-partnership",
  WorksWithUgc = "works-with-ugc",
  WorksWithInfluencer = "works-with-influencer",
  RunsTiktokAds = "runs-tiktok-ads",
  HasAds = "has-ads",
  EmergingBrand = "emerging-brand",
}

export const CONTACT_TYPES = [
  {
    key: ContactType.All,
    name: "All",
  },
  {
    key: ContactType.NonGenericEmails,
    name: "Has a Direct Email",
    label: "These brands have a non-generic contact email available",
    paidOnly: true,
  },
  {
    key: ContactType.NotContactedThisWeek,
    name: "Not Contacted This Week",
    label: "These brands have not been contacted by any Bento user this week",
    paidOnly: true,
  },
  {
    key: ContactType.HasContacts,
    name: "Any Email Available",
    paidOnly: true,
  },
  {
    key: ContactType.NeverPitched,
    name: "Never Pitched by You",
    label: "You have never pitched to these brands",
    paidOnly: true,
  },
];

export const COMPANY_TYPES = [
  { key: CompanyType.All, name: "All" },
  {
    key: CompanyType.VerifiedPaidPartnership,
    name: "Partners With Creators",
    paidOnly: true,
  },

  {
    key: CompanyType.WorksWithUgc,
    name: "Works with UGC Creators",
    paidOnly: true,
  },
  {
    key: CompanyType.WorksWithInfluencer,
    name: "Works with Influencers",
    paidOnly: true,
  },
  {
    key: CompanyType.RunsTiktokAds,
    name: "Runs TikTok Ads",
    paidOnly: true,
  },
  {
    key: CompanyType.HasAds,
    name: "Has Ads",
    paidOnly: true,
  },
  {
    key: CompanyType.EmergingBrand,
    name: "Is Emerging",
    adminOnly: true,
  },
];

export enum Location {
  All = "all",
  UnitedStates = "us",
  Canada = "ca",
  UK = "gb",
  Australia = "au",
  France = "fr",
  Germany = "de",
  Italy = "it",
  Spain = "es",
  India = "in",
  Europe = "europe",
  Asia = "asia",
  NorthAmerica = "north america",
  SouthAmerica = "south america",
  Oceania = "oceania",
  Africa = "africa",
  International = "international",
}

export enum CompensationType {
  affiliate = "affiliate",
  tiktok_shop = "tiktok_shop",
  cash = "cash",
  exposure = "exposure",
  products_or_services = "products_or_services",
  other = "other",
}

export const LOCATIONS = [
  { key: Location.All, name: "All" },
  { key: Location.UnitedStates, name: "United States" },
  { key: Location.Canada, name: "Canada" },
  { key: Location.UK, name: "UK" },
  { key: Location.Australia, name: "Australia" },
  { key: Location.France, name: "France" },
  { key: Location.Germany, name: "Germany" },
  { key: Location.Italy, name: "Italy" },
  { key: Location.Spain, name: "Spain" },
  { key: Location.India, name: "India" },
  { key: Location.NorthAmerica, name: "North America" },
  { key: Location.Europe, name: "Europe" },
  { key: Location.Asia, name: "Asia" },
  { key: Location.Africa, name: "Africa" },
  { key: Location.Oceania, name: "Oceania" },
  { key: Location.SouthAmerica, name: "South and Central America" },
  { key: Location.International, name: "International" },
];

export enum FollowingSize {
  All = "all",
  verified = "verified",
  xxs = "xxs",
  xs = "xs",
  sm = "sm",
  md = "md",
  lg = "lg",
  xl = "xl",
}

export enum TargetAge {
  All = "all",
  teenager = "teenager",
  twenties = "20s",
  thirties = "30s",
  middle_aged = "middle-aged",
  senior = "senior",
}

export enum TargetGender {
  all = "all",
  male = "male",
  female = "female",
}

export const FollowingSizeRange: {
  [key: string]: { min: number; max: number };
} = {
  [FollowingSize.verified]: { min: 0, max: 1000000000 },
  [FollowingSize.xxs]: { min: 0, max: 10000 },
  [FollowingSize.xs]: { min: 10000, max: 100000 },
  [FollowingSize.sm]: { min: 100000, max: 500000 },
  [FollowingSize.md]: { min: 500000, max: 1000000 },
  [FollowingSize.lg]: { min: 1000000, max: 10000000 },
  [FollowingSize.xl]: { min: 10000000, max: 1000000000 },
};

export const TargetAgeRange: {
  [key: string]: { min: number; max: number };
} = {
  [TargetAge.teenager]: { min: 13, max: 19 },
  [TargetAge.twenties]: { min: 20, max: 29 },
  [TargetAge.thirties]: { min: 30, max: 39 },
  [TargetAge.middle_aged]: { min: 40, max: 55 },
  [TargetAge.senior]: { min: 56, max: 150 },
};

export const TARGET_GENDERS = [
  {
    key: TargetGender.all,
    name: "All",
  },
  {
    key: TargetGender.female,
    name: "Female",
  },
  {
    key: TargetGender.male,
    name: "Male",
  },
];

export const TARGET_AGE_RANGES = [
  {
    key: TargetAge.All,
    name: "All",
  },
  {
    key: TargetAge.teenager,
    name: "Teenager",
  },
  {
    key: TargetAge.twenties,
    name: "20s",
  },
  {
    key: TargetAge.thirties,
    name: "30s",
  },
  {
    key: TargetAge.middle_aged,
    name: "Middle-aged",
  },
  {
    key: TargetAge.senior,
    name: "Senior",
  },
];

export const FOLLOWING_SIZES = [
  { key: FollowingSize.All, name: "All" },
  {
    key: FollowingSize.verified,
    name: "Verified",
    adminOnly: true,
  },
  {
    key: FollowingSize.xxs,
    name: "Below 10K",
    paidOnly: true,
  },
  {
    key: FollowingSize.xs,
    name: "10K - 100K",
    paidOnly: true,
  },
  {
    key: FollowingSize.sm,
    name: "100K - 500K",
    paidOnly: true,
  },
  {
    key: FollowingSize.md,
    name: "500K - 1M",
    paidOnly: true,
  },
  {
    key: FollowingSize.lg,
    name: "1M - 10M",
    paidOnly: true,
  },
  { key: FollowingSize.xl, name: "10M+", paidOnly: true },
];

export const COMPENSATION_TYPES = [
  { key: CompensationType.cash, name: "Cash" },
  { key: CompensationType.products_or_services, name: "Products or Services" },
  { key: CompensationType.affiliate, name: "Affiliate" },
  { key: CompensationType.exposure, name: "Exposure" },
  { key: CompensationType.other, name: "Other" },
];

export const DISCOVER_FILTER: { [key: string]: Map } = {
  [SearchParams.TYPE_DISCOVER]: {
    [CompanyType.All]: { name: "All", params: SearchParams.TYPE_DISCOVER },
    [CompanyType.VerifiedPaidPartnership]: {
      name: "Partners With Creators",
      params: SearchParams.TYPE_DISCOVER,
    },
    [CompanyType.WorksWithUgc]: {
      name: "Works with UGC Creators",
      params: SearchParams.TYPE_DISCOVER,
    },
    [CompanyType.WorksWithInfluencer]: {
      name: "Works with Influencers",
      params: SearchParams.TYPE_DISCOVER,
    },
    [CompanyType.RunsTiktokAds]: {
      name: "Runs TikTok Ads",
      params: SearchParams.TYPE_DISCOVER,
    },
    [CompanyType.HasAds]: {
      name: "Has Ads",
      params: SearchParams.TYPE_DISCOVER,
    },
    [CompanyType.EmergingBrand]: {
      name: "Emerging Brand",
      params: SearchParams.TYPE_DISCOVER,
    },
  },
  [SearchParams.CONTACT_DISCOVER]: {
    [ContactType.All]: { name: "All", params: SearchParams.CONTACT_DISCOVER },
    [ContactType.NotContactedThisWeek]: {
      name: "Not Contacted This Week",
      params: SearchParams.CONTACT_DISCOVER,
    },
    [ContactType.NonGenericEmails]: {
      name: "Has a Direct Email",
      params: SearchParams.CONTACT_DISCOVER,
    },
    [ContactType.HasContacts]: {
      name: "Any Email Available",
      params: SearchParams.CONTACT_DISCOVER,
    },
    [ContactType.NeverPitched]: {
      name: "Never Pitched by You",
      params: SearchParams.CONTACT_DISCOVER,
    },
  },
  [SearchParams.LOCATION_DISCOVER]: {
    [Location.All]: { name: "All", params: SearchParams.LOCATION_DISCOVER },
    [Location.UnitedStates]: {
      name: "United States",
      params: SearchParams.LOCATION_DISCOVER,
    },
    [Location.Canada]: {
      name: "Canada",
      params: SearchParams.LOCATION_DISCOVER,
    },
    [Location.UK]: { name: "UK", params: SearchParams.LOCATION_DISCOVER },
    [Location.Australia]: {
      name: "Australia",
      params: SearchParams.LOCATION_DISCOVER,
    },
    [Location.France]: {
      name: "France",
      params: SearchParams.LOCATION_DISCOVER,
    },
    [Location.Germany]: {
      name: "Germany",
      params: SearchParams.LOCATION_DISCOVER,
    },
    [Location.Italy]: {
      name: "Italy",
      params: SearchParams.LOCATION_DISCOVER,
    },
    [Location.Spain]: {
      name: "Spain",
      params: SearchParams.LOCATION_DISCOVER,
    },
    [Location.India]: {
      name: "India",
      params: SearchParams.LOCATION_DISCOVER,
    },
    [Location.Europe]: {
      name: "Europe",
      params: SearchParams.LOCATION_DISCOVER,
    },
    [Location.Asia]: { name: "Asia", params: SearchParams.LOCATION_DISCOVER },
    [Location.Africa]: {
      name: "Africa",
      params: SearchParams.LOCATION_DISCOVER,
    },
    [Location.NorthAmerica]: {
      name: "North America",
      params: SearchParams.LOCATION_DISCOVER,
    },
    [Location.SouthAmerica]: {
      name: "South and Central America",
      params: SearchParams.LOCATION_DISCOVER,
    },
    [Location.Oceania]: {
      name: "Oceania",
      params: SearchParams.LOCATION_DISCOVER,
    },
    [Location.International]: {
      name: "International",
      params: SearchParams.LOCATION_DISCOVER,
    },
  },
  [SearchParams.FOLLOWING_DISCOVER]: {
    [FollowingSize.All]: {
      name: "All",
      params: SearchParams.FOLLOWING_DISCOVER,
    },
    [FollowingSize.verified]: {
      name: "Verified Instagram",
      params: SearchParams.FOLLOWING_DISCOVER,
    },
    [FollowingSize.xxs]: {
      name: "Below 10K",
      params: SearchParams.FOLLOWING_DISCOVER,
    },
    [FollowingSize.xs]: {
      name: "10K - 100K",
      params: SearchParams.FOLLOWING_DISCOVER,
    },
    [FollowingSize.sm]: {
      name: "100K - 500K",
      params: SearchParams.FOLLOWING_DISCOVER,
    },
    [FollowingSize.md]: {
      name: "500K - 1M",
      params: SearchParams.FOLLOWING_DISCOVER,
    },
    [FollowingSize.lg]: {
      name: "1M - 10M",
      params: SearchParams.FOLLOWING_DISCOVER,
    },
    [FollowingSize.xl]: {
      name: "10M+",
      params: SearchParams.FOLLOWING_DISCOVER,
    },
  },
  [SearchParams.AGE_DISCOVER]: {
    [TargetAge.All]: {
      name: "All",
      params: SearchParams.AGE_DISCOVER,
    },
    [TargetAge.teenager]: {
      name: "Teenager",
      params: SearchParams.AGE_DISCOVER,
    },
    [TargetAge.twenties]: {
      name: "20s",
      params: SearchParams.AGE_DISCOVER,
    },
    [TargetAge.thirties]: {
      name: "30s",
      params: SearchParams.AGE_DISCOVER,
    },
    [TargetAge.middle_aged]: {
      name: "Middle-aged",
      params: SearchParams.AGE_DISCOVER,
    },
    [TargetAge.senior]: {
      name: "Senior",
      params: SearchParams.AGE_DISCOVER,
    },
  },
  [SearchParams.GENDER_DISCOVER]: {
    [TargetGender.all]: {
      name: "All",
      params: SearchParams.GENDER_DISCOVER,
    },
    [TargetGender.female]: {
      name: "Female",
      params: SearchParams.GENDER_DISCOVER,
    },
    [TargetGender.male]: {
      name: "Male",
      params: SearchParams.GENDER_DISCOVER,
    },
  },
  [SearchParams.SORT_DISCOVER]: {
    [Sort.Recommended]: {
      name: "Recommended",
      params: SearchParams.SORT_DISCOVER,
    },
    [Sort.RecentlyAdded]: {
      name: "Recently Added",
      params: SearchParams.SORT_DISCOVER,
    },
  },
  [SearchParams.COMPENSATION_DISCOVER]: {
    [CompensationType.cash]: {
      name: "Cash",
      params: SearchParams.COMPENSATION_DISCOVER,
    },
    [CompensationType.products_or_services]: {
      name: "Products or Services",
      params: SearchParams.COMPENSATION_DISCOVER,
    },
    [CompensationType.affiliate]: {
      name: "Affiliate",
      params: SearchParams.COMPENSATION_DISCOVER,
    },
    [CompensationType.exposure]: {
      name: "Exposure",
      params: SearchParams.COMPENSATION_DISCOVER,
    },
    [CompensationType.other]: {
      name: "Other",
      params: SearchParams.COMPENSATION_DISCOVER,
    },
  },
};

export enum SearchParamsValue {
  OPPORTUNITY_DRAWER = "opportunityDrawer",
  REMINDER_DRAWER = "reminderDrawer",
  CONTACT_DRAWER = "contactDrawer",

  // Potential search params values for QUERY_TASK_USER
  QUERY_TASK_USER_CURRENT = "queryTaskUserCurrent",

  TAB_CONTACT = "tabContact",
  TAB_OPPORTUNITY = "tabOpportunity",
  TAB_INTERACTION = "tabInteraction",
}

export type BentoBrandSearchParameters = {
  query?: string;
  sort: Sort;
  category_ids?: Array<string>;
  company_types?: Array<CompanyType>;
  contact_types?: Array<ContactType>;
  locations?: Array<Location>;
  geopoints?: Array<string>;
  min_instagram_followers?: number;
  max_instagram_followers?: number;
  compensation_types?: Array<CompensationType>;
  target_genders?: Array<TargetGender>;
  min_target_age?: number;
  max_target_age?: number;
};

export const BESearchParametersToFEParams: { [key: string]: string } = {
  sort: SearchParams.SORT_DISCOVER,
  locations: SearchParams.LOCATION_DISCOVER,
  company_types: SearchParams.TYPE_DISCOVER,
  contact_types: SearchParams.CONTACT_DISCOVER,
  geopoints: SearchParams.GEOPOINTS_DISCOVER,
};

// Tasks are for influencer, Reminders are for Founders
export type Reminder = {
  id: string | null;
  status: ReminderStatus;
  userId?: number;
  contactId: number | null;
  user?: OrganizationUser;
  contact?: Contact;
  dueAt?: any; // dueAt can either be a date object or epoch number
  notes?: string;
  opportunities?: Opportunity[];
};

export enum ReminderStatus {
  PENDING = "pending",
  COMPLETE = "complete",
}

export type Task = {
  id?: number;
  status?: TaskStatus;
  description?: string;
  dueAt?: number | string; // epochTime or local datePicker
  brand?: Brand;
  brandId?: number;
  type?: TaskType;
  projectId?: number;
  project?: Project;
  emailDraftId?: number;
  emailDraft?: EmailDraft;
  gmailThreadId?: number;
  messages?: Message[];
  bentoBrandId?: number;
  updatedAt?: number;
  outreachContactId?: number;
  outreachContact?: OutreachContact;
};

export enum TaskStatus {
  PENDING = "pending",
  COMPLETED = "completed",
}

export type Interaction = {
  id?: number;
  userId?: number;
  contactId?: number;
  contact?: Contact;
  interactedAt?: string;
  notes?: string;
  recordingLink?: string;
  recordingSummary?: string;
};

export type UserIntegration = {
  id: number;
  provider: string;
  connected: boolean;
  email?: string;
  integrationError?: string;
  partnershipsSearchedAt?: number;
  partnershipsSearchCompletedAt?: number;
  isCustomDomain?: boolean;
  userId: number;
};

export enum CsvKey {
  CONTACT_PERSON = "contactPerson",
  CONTACT_EMAIL = "contactEmail",
  CONTACT_PERSON_TITLE = "contactPersonTitle",
  MESSAGE = "message",
  STATUS = "status",
}

export enum CsvLinkKey {
  INSTAGRAM = "instagram",
}

export enum MessageStatus {
  PENDING = "pending",
  PROCESSING = "processing",
  FAILED = "failed",
  COMPLETED = "completed",
  BOUNCED = "bounced",
  CANCELLED = "cancelled",
}

export type PreviewMessage = {
  templateId?: number;
  templateName?: string;
  body: string;
  subject?: string;
  attachment?: string;
  missingValues?: string[];
  contentType: TemplateContentType;

  isEdited?: boolean;
};

export type PreviewMessageSettings = {
  saveTemplateEdit?: boolean;
};

export enum ReactionType {
  LIKE = "like",
  DISLIKE = "dislike",
}

export type Reaction = {
  id: number;
  reactionType: ReactionType;
};

export enum OutreachContactAutomatedStatus {
  new = "new",
  contacted = "contacted",
  in_discussion = "in_discussion",
  referred = "referred",
  reach_out_later = "reach_out_later",
  declined = "declined",
  invalid = "invalid",
  automated = "automated",
}

export enum OutreachContactStatus {
  pending = "pending",
  bounced = "bounced",
  no_response = "no_response",
  received_response = "received_response",
  interested = "interested",
  not_interested = "not_interested",
}

export type Email = {
  attachment?: string;
  body?: string;
  subject?: string;
  completedAt?: number;
  createdAt: number;
  contentType?: TemplateContentType;
  gmailThreadId?: number;
  outreachContactId?: number;
  scheduledAt: number;
  sendAt: number;
  status: MessageStatus;
  id: number;
  userId: number;
  toEmail?: string;
  ccContacts?: EmailAddress[];
  templateId?: number;

  // used in drafts
  isChanged?: boolean;

  // use in preview scheduled email
  missingValues?: string[];
};

export type EmailActivity = {
  userDevice: string;
  formattedAddress: string;
  createdAt: number;
  id: number;
};

export type OutreachContact = {
  id?: number;
  organizationId?: number;
  brand?: Brand;
  contactName: string;
  contactTitle: string;
  email?: string;
  instagramUsername: string;
  contactMethod: ContactMethod;
  createdAt?: number;
  latestMessageStatus?: MessageStatus | null;
  reactions?: Reaction[];
  bentoBrand?: BentoBrand;
  bentoBrandId?: number;
  bentoContactId?: number;
  bentoBrandContents?: BentoBrandContent[];
  automatedStatus?: OutreachContactAutomatedStatus;
  status?: OutreachContactStatus;
  fromVa?: boolean;
  smartTask?: Task;
  updatedAt?: number;
  lastGmailMessageDate?: number;
  lastGmailThreadId?: string;
  lastGmailThreadParticipants?: string;
  lastGmailThreadLength?: number;
  lastGmailMessageSubject?: string;
  lastGmailMessageSnippet?: string;
  latestEmail?: Email;
  isPinned?: boolean;
  isPinnedForTracking?: boolean;
  isImportant?: boolean;
  isArchived?: boolean;
  lastEmailOpenCount: number;
  lastEmailOpenedAt?: number;
  hasTrackedEmails?: boolean;
  propertyValues?: { [key: number]: any };
  nextPendingTask?: Task;
  hasResponded?: boolean;

  emails?: Email[];

  latestSequenceUuid?: string;
  latestSequenceSentCount?: number;
  latestSequenceTotalCount?: number;
  latestSequenceNextSendAt?: number;
  latestOutreachTemplateId?: number;

  bentoContact?: BentoContact;
  source?: string;
  savedBrand?: SavedBrand;
};

export type OutreachEmailDraft = {
  emailTemplateId?: number;
  emailTemplateName?: string;
  subject?: string;
  body: string;
  attachment?: string;
  contentType: TemplateContentType;
};

export type OutreachDraft = {
  bentoBrand: BentoBrand;
  bentoBrandId: number;
  bentoContactId?: number;
  contactEmail?: string;
  contactName?: string;
  contactTitle?: string;
  emailDrafts: OutreachEmailDraft[];
  isAutogenerated?: boolean;
  createdAt?: number;
  id?: number;
};

export type GmailThread = {
  id: string;
  messages: Message[];
  pendingEmailsCount: number;
};

export enum ContentPlatform {
  instagram = "instagram",
}

export enum MetadataType {
  tags = "tags",
  size = "size",
  location = "location",
}

export enum MetadataLocationType {
  country = "country",
  continent = "continent",
}

export type BentoBrandMetadataTags = {
  longitude?: number;
  latitude?: number;
  type: MetadataType;
  value: string;
  locationType?: MetadataLocationType;
};

export type BentoBrandPartnership = {
  created_at: number;
  updated_at: number;
  compensation_status: string | null;
  compensation_types: CompensationType[];
  organization_country_code: string | null;
  country?: string | null;
  organization_search_filter_ids: string[];
  status: string;
};

export enum BentoBrandTier {
  tier_one = "tier_one",
  tier_two = "tier_two",
}

export type BentoBrand = {
  countryCode?: string;
  createdAt: number;
  id: number;
  brandName: string;
  normalizedName: string;
  website?: string;
  logoUrl?: string;
  backgroundLogoColor?: string;
  mainImageUrl?: string;
  brandImageUrls?: string[];
  tags: string;
  instagramUsername?: string;
  isArchived?: boolean;
  doNotRecommended?: boolean;
  hasValidatedPartnership?: boolean;
  worksWithUgc?: boolean;
  worksWithInfluencer?: boolean;
  isInternational?: boolean;
  hasBrandNotification?: boolean;
  hasContacts?: boolean;
  metadata?: BentoBrandMetadataTags[];
  pricePoint?: string;
  size?: string;
  tier?: BentoBrandTier;

  twitterUsername?: string;
  facebookUsername?: string;
  pinterestUsername?: string;
  tiktokUsername?: string;
  youtubeUrl?: string;
  linkedinUrl?: string;
  mainImageSource?: string;

  aboutSummary?: string;
  aboutReferenceLink?: string;
  targetAge?: string;
  targetGender?: string;
  targetPricePoint?: string;
  targetPainPoint?: string;
  targetSummary?: string;
  targetReferenceLinks?: string[];
  targetLocation?: string;
  compensationTypes?: CompensationType[];
  partnerships?: BentoBrandPartnership[];
  verificationPriority?: number;
  verificationNotes?: string;
  verificationStatus?: BentoBrandVerificationStatus;

  // ElasticSearch Property
  emailedBrand?: boolean;
  isSavedBrand?: boolean;
  contactedRecentlyAmount?: number;
  countries?: string[];
  continents?: string[];
  locationNames?: string[];
  tagsAutocomplete?: string[];
  instagramFollowerCount?: number;
  hasTiktokAds?: boolean;
  hasAds?: boolean;
  isEmerging?: boolean;
  hasNonGenericContacts?: boolean;
  mainCategoryNames?: string[];
  secondaryCategoryNames?: string[];
  mainCategoryIds?: number[];
  secondaryCategoryIds?: number[];

  // SavedBrand property
  hasOutreachDraft?: boolean;
  latestEmailCompletedAt?: string;
  earliestPendingSendAt?: string;
  orgHasOutreachContact?: boolean;
  isAdmin?: boolean;
  createdByAutomation?: boolean;
  collections?: SavedBrandCollection[];
  // BulkSend property
  sequenceUuid?: string;

  // Verification only
  flags?: BentoBrandFlag[];
};

export type BentoBrandFlag = {
  id: number;
  bentoBrandId: number;
  bentoBrandRuleId: number;
  bentoBrandConstraintId: number;
  orgUserId: number;
  source: BentoBrandFlagSource;
  status: BentoBrandFlagStatus;
  priority?: number;
  notes?: string;
  submittedReasons?: string[];
  submittedComment?: string;
  flaggedData?: string[];
};

export type BentoSearchFilter = {
  id: number;
  name: string;
  keywords: string[];
  vaDisplayName?: string;
  enableForExclude: boolean;
  enableForInclude: boolean;
  enableForVaInclude: boolean;
  enableForVaExclude: boolean;
  minTargetAge?: number;
  maxTargetAge?: number;
  targetGender?: TargetGender;
};

export enum BentoContactResponseType {
  wrong_contact = "wrong_contact",
  not_in_company = "not_in_company",
  stop_emailing = "stop_emailing",
  automated = "automated",
  bounced = "bounced",
  not_interested = "not_interested",
  reach_out_later = "reach_out_later",
  not_interested_reach_out_later = "not_interested_reach_out_later",
  fill_out_form = "fill_out_form",
  only_tiktok_shop = "only_tiktok_shop",
  only_influencer_marketing = "only_influencer_marketing",
  wrong_location = "wrong_location",
  shared_profile = "shared_profile",
  referred_someone_else = "referred_someone_else",
  no_content_marketing = "no_content_marketing",
  send_socials = "send_socials",
  interested = "interested",
  products_or_services_only = "products_or_services_only",
  details_discussion = "details_discussion",
  schedule_call = "schedule_call",
  compensation_discussion = "compensation_discussion",
  cash_compensation_discussion = "cash_compensation_discussion",
}

export enum BentoBrandVerificationStatus {
  pending = "pending",
  completed = "completed",
  changed = "changed",
}

export enum BentoBrandFlagStatus {
  pending = "pending",
  accepted = "accepted",
  partially_accepted = "partially_accepted",
  changed = "changed",
  dismissed = "dismissed",
  archived = "archived",
}

export enum BentoBrandFlagSource {
  rule = "rule",
  constraint = "constraint",
  user = "user",
  admin = "admin",
  system = "system",
}

export enum BentoContactArchiveReason {
  contact_requested = "contact_requested",
  bounced = "bounced",
  invalid_format = "invalid_format",
  low_hunter_score = "low_hunter_score",
  consecutive_blocks = "consecutive_blocks",
  spam = "spam",
}

export type BentoContact = {
  isArchived?: boolean;
  id?: number;
  name?: string;
  title?: string;
  email: string;
  score?: number;
  location?: string;
  bentoBrand?: BentoBrand;
  bentoBrandId?: number;
  bentoContactResponses?: BentoContactResponse[];
  source?: BentoContactSource;
  sourcedFromUserInbox?: boolean;
  lastSentToAt?: number;
  bestResponseType?: BentoContactResponseType;
  archiveReason?: BentoContactArchiveReason;
};

export type BentoContactResponse = {
  id: number;
  userIntegrationId?: number;
  userIntegration?: UserIntegration;
  gmailThreadId: string;
  responseTypes?: string[];
};

export enum BentoContactSource {
  gmail = "gmail",
  gmail_import = "gmail_import",
  referral = "referral",
}

export type NewBentoContact = {
  name?: string;
  email: string;
  title?: string;
};

export type BrandRecommendation = {
  id: number;
  bentoBrand: BentoBrand;
  bentoBrandId: number;
  socialMediaPost?: SocialMediaPost;
  source: BrandRecommendationSource | null;
  archiveReason?: BrandRecommendationArchiveReason;
};

export enum BrandRecommendationArchiveReason {
  DO_NOT_RECOMMEND = "do_not_recommend",
}

export type SocialMediaPost = {
  id: number;
  postAssetTitle?: string;
  postDescription?: string;
  postUrl?: string;
  postAssetUrl?: string;
};

export enum BrandRecommendationSource {
  SOCIAL_MEDIA_POST = "social_media_post",
  TOP_MATCH = "top_match",
  USER_ARCHIVE = "user_archive",
}

export type SavedBrand = {
  id?: number;
  bentoBrand: BentoBrand;
  bentoBrandId: number;
  createdAt: number;
  createdByAutomation?: boolean;
  isAdmin?: boolean;
};

export type SavedBrandCollection = {
  id: number;
  name: string;
  sentCount: number;
  unsentCount: number;
  isDefaultForVa?: boolean;
  isInstagramCollection?: boolean;
  excludeFromDefaultVa?: boolean;
  sentBrands?: SavedBrand[];
  unsentBrands?: SavedBrand[];
};

export type SavedBrandCollectionAssociation = {
  organizationId: number;
  savedBrandCollectionId: number;
  bentoBrandId: number;
};

export type TikTokAd = {
  id: string;
  ctrPercentile?: number;
  conversionCountPercentile?: number;
  shareCount?: number;
  likeCount?: number;
  title?: string;
  uploadedVideoUrl: string;
};

export type BentoBrandContent = {
  id?: number;
  socialMediaPostId?: number;
  bentoBrandId: number;
  platform: ContentPlatform;
  url: string;
  username: string;
  followerCount: number;
  followingCount?: number;
  postCount?: number;
  title?: string;
  thumbnailUrl?: string;
  likeCount?: number;
  commentCount?: number;
};

export type BentoBrandProduct = {
  id: number;
  name: string;
  bentoBrandId: number;
  description?: string;
  onlineStoreUrl?: string;
  purchaseLocation?: string;
  imageUrl?: string;
};

export type Brand = {
  bentoBrandId?: number;
  id: number;
  organizationId?: number;
  name: string;
  projects?: Project[];
  brandContacts?: BrandContact[];
  updatedAt?: number;
  website?: string;
  instagramUsername?: string;
  hasResponded?: boolean;
  lastGmailMessageDate?: number;
  compensationTypes?: CompensationType[];
};

export type BrandContact = {
  id: number;
  brandId?: number;
  name?: string;
  title?: string;
  email?: string;
};

export type Project = {
  id?: number;
  brandId?: number;
  brandName?: string;
  name: string;
  notes?: string;
  brandContacts?: BrandContact[];
  updatedAt?: number;
  brand?: Brand;
  partnershipType?: PartnershipType;
  tasks?: Task[];
};

export enum ProjectSort {
  NAME = "name:asc",
  LAST_MODIFIED = "updated_at:desc",
}

export enum ProjectFilter {
  PAID = "paid",
  GIFTED = "gifted",
  ALL = "all",
}

export enum PartnershipType {
  PAID = "paid",
  GIFTED = "gifted",
}

export enum TaskType {
  OUTREACH = "outreach",
  PROJECT = "project",
  EMAIL = "email",
}

export enum TaskSort {
  DUE_DATE = "due_at:asc",
}

export enum ProjectCreationSource {
  BRAND_RECOMMENDATION = "brand_recommendation",
  SEARCH_RESULT = "search_result",
}

export type UserNotifications = {
  optInEmail?: boolean;
  optInPhone?: boolean;
};

export enum GmailScope {
  READ = "https://www.googleapis.com/auth/gmail.readonly",
  SEND = "https://www.googleapis.com/auth/gmail.send",
}

export enum RequestStatus {
  pending = "pending",
  found = "found",
  not_found = "not_found",
}

export enum RequestType {
  user_requests = "user_requests",
  brand_notifications = "brand_notifications",
  contact_notifications = "contact_notifications",
}

export type CombinedRequest = {
  additionalContactNotificationStatus?: RequestStatus;
  additionalContactNotificationUpdatedAt?: number;
  bentoBrand?: BentoBrand;
  bentoBrandIdOrUserRequestDescription?: string;
  brandNotificationStatus?: RequestStatus;
  brandNotificationUpdatedAt?: string;
  earliestPendingSendAt?: string;
  hasFoundRequest?: boolean;
  latestEmailCompletedAt?: string;
  latestRequestUpdatedAt?: string;
  userRequestStatus?: RequestStatus;
  userRequestUpdatedAt?: string;
};

export type UserRequest = {
  id?: number;
  description: string;
  source: UserRequestSource;
  status: RequestStatus;
  createdAt: number;
  userId: number;
  bentoBrand?: BentoBrand;
};

export type BrandNotification = {
  bentoBrand?: BentoBrand;
  status: RequestStatus;
  bentoBrandId?: number;
};

export type AdditionalContactNotification = {
  updatedAt: number | undefined;
  latestEmailCompletedAt: any;
  earliestPendingSendAt: any;
  bentoBrand?: BentoBrand;
  status: RequestStatus;
  bentoBrandId?: number;
  bentoContactId?: number;
};

export enum UserRequestSource {
  SOURCE_TOGGLE = "no_results_toggle",
  SOURCE_REQUEST_BRAND = "request_brand",
}

export type ChatMessage = {
  countsTowardQuota: boolean;
  id?: number;
  message: string;
  role: "user" | "assistant";
  bentoBrandId?: number;
};

export enum ChatMessageAction {
  personalize = "personalize",
  openChat = "open",
}

export enum PropertyType {
  shortText = "short_text",
  longText = "long_text",
  attachment = "attachment",
  checkbox = "checkbox",
  multipleSelect = "multiple_select",
  singleSelect = "single_select",
  number = "number",
  date = "date",
  dateTime = "date_time",
}

export const PropertyTypesList = [
  PropertyType.shortText,
  PropertyType.longText,
  PropertyType.attachment,
  PropertyType.checkbox,
  PropertyType.multipleSelect,
  PropertyType.singleSelect,
  PropertyType.number,
  PropertyType.date,
  PropertyType.dateTime,
];

export const PropertyTypeLabel: {
  [key in PropertyType]: string;
} = {
  [PropertyType.shortText]: "Short Text",
  [PropertyType.longText]: "Long Text",
  [PropertyType.number]: "Number",
  [PropertyType.attachment]: "Attachment",
  [PropertyType.checkbox]: "Checkbox",
  [PropertyType.multipleSelect]: "Multiple Select",
  [PropertyType.singleSelect]: "Single Select",
  [PropertyType.date]: "Date",
  [PropertyType.dateTime]: "Date Time",
};

export type SelectPropertyOption = {
  id: string;
  name: string;
  color?: string;
};

export type ContactProperty = {
  id?: number;
  name: string;
  type?: PropertyType;
  selectOptions?: SelectOption[];
  automatedProperty?: AutomatedContactProperty;
};

export enum AutomatedContactProperty {
  stage = "stage",
  status = "status",
  interactions = "interactions",
}

export enum BuiltInContactPropertyName {
  contactName = "contact_name",
  contactTitle = "contact_title",
  email = "email",
  brandName = "brand_name",
  createdAt = "created_at",
  brandHasResponded = "brand_has_responded",
  brandLastGmailMessageDate = "brand_last_gmail_message_date",
}

export const BuildInContactPropertyNamesList = [
  BuiltInContactPropertyName.email,
  BuiltInContactPropertyName.brandName,
  BuiltInContactPropertyName.contactName,
  BuiltInContactPropertyName.contactTitle,
  BuiltInContactPropertyName.createdAt,
  BuiltInContactPropertyName.brandHasResponded,
  BuiltInContactPropertyName.brandLastGmailMessageDate,
];

export const PropertyNameLabel: {
  [key in BuiltInContactPropertyName]: string;
} = {
  [BuiltInContactPropertyName.contactName]: "Contact Name",
  [BuiltInContactPropertyName.contactTitle]: "Contact Title",
  [BuiltInContactPropertyName.email]: "Contact Email",
  [BuiltInContactPropertyName.brandName]: "Brand Name",
  [BuiltInContactPropertyName.createdAt]: "Created On",
  [BuiltInContactPropertyName.brandHasResponded]: "Brand Has Responded",
  [BuiltInContactPropertyName.brandLastGmailMessageDate]:
    "Last Email Sent or Received From Brand",
};

export const TitleMap: { [key: string]: string } = {
  ...PropertyNameLabel,
  last_gmail_message_date: "Date",
  smart_task: "Tasks",
  is_important: "Awaiting Your Response",
  send_follow_up: "Send Follow-Up",
  latest_sequence_status: "Status",
  latest_outreach_template: "Templated Used",
  actions: "Actions",
  move_brand_to: "Move Brand",
  last_email_open_count: "Opens",
  email_thread: "Email Thread",
};

export type MatchColumn = {
  column_index: number;
  contact_property_name: BuiltInContactPropertyName | null;
  contact_property_id: number | null;
  skip_column: boolean;
};

export type RowImport = {
  row: number;
  email?: string;
  skip_reason?: string;
};

export type FailedReason = {
  row: number;
  column: number;
  value: string;
  contact_property_id: number;
  reason: string;
  email?: string;
};

export type UploadSummary = {
  new: RowImport[];
  update: RowImport[];
  skip: RowImport[];
  failed: FailedReason[];
};

export type NumbersMap = { [key: number]: number | undefined };

export type SavedBrandCollectionsMap = {
  [key in number]: SavedBrandCollection;
};

export type NameMap = { [key: number]: string };
export enum SavedBrandStatus {
  unsent = "unsent",
  sent = "sent",
}

export type Automation = {
  id?: number;
  count: number;
  dayOfWeek: number;
  options: SaveBrandAutomationOptions;
  searchParameters: BentoBrandSearchParameters;
  isTopNiche: boolean;
  status: AutomationStatus;
  organizationNicheId?: number;
  isAutogenerated?: boolean;
};

export enum AutomationStatus {
  active = "active",
  inactive = "inactive",
}

export type SaveBrandAutomationOptions = {
  collection_option: "none" | "new" | "existing";
  collection_id?: number;
};

export type EmailSequence = {
  bentoBrandId: number;
  sequenceUuid?: string;
  fallbackTemplateIds?: number[];

  // Only used on FE
  category: string;
  bentoBrand: BentoBrand;
};

export type EmailSequenceMap = { [key: number]: EmailSequence };

export enum SendOption {
  same = "same",
  by_category = "by_category",
  by_automation = "by_automation",
}

export enum CampaignStatus {
  in_progress = "in_progress",
  complete = "complete",
  pending_approval = "pending_approval",
  expired = "expired",
}

export type Campaign = {
  createdByAdmin: boolean;
  id: number;
  name: string;
  createdAt: number;
  savedBrandCollectionId?: number;
  isAllSavedCollection?: number;
  searchParameters?: BentoBrandSearchParameters;
  emailSetting?: EmailSettings;
  status: CampaignStatus;
  daysOfWeek: number[];
  timezone: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  campaignBrandCount?: number;
  agentSessionId?: number;
};

export enum CampaignBrandStatus {
  pending = "pending",
  scheduled = "scheduled",
  no_contact = "no_contact",
  archived = "archived",
}

export type CampaignBrand = {
  bentoBrand: BentoBrand;
  status: CampaignBrandStatus;
  outreachContact: OutreachContact;
};

export type CampaignStats = {
  outreachCount: number;
  followUpCount: number;
  replyCount: number;
  openedCount: number;
  openedPercentage: number;
  respondedCount: number;
  respondedPercentage: number;
};

export enum ActionItemType {
  rate_discussion_follow_up = "rate_discussion_follow_up",
  past_partnership_check_in = "past_partnership_check_in",
  out_of_office_follow_up = "out_of_office_follow_up",
  new_contact_outreach = "new_contact_outreach",
  brand_response = "brand_response",
}

export enum ActionItemStatus {
  needs_verification = "needs_verification",
  pending = "pending",
  completed = "completed",
  archived = "archived",
}

export enum ActionItemArchiveReason {
  user_archived = "user_archived",
  max_follow_ups_sent = "max_follow_ups_sent",
  replaced = "replaced",
  message_sent_in_thread = "message_sent_in_thread",
}

export enum ActionItemVerificationError {
  invalid_integration = "invalid_integration",
  failed_to_fetch_thread = "failed_to_fetch_thread",
}

export type ActionItem = {
  id: number;
  createdAt: number;
  outreachContactId: number;
  gmailThreadId: string;
  emailDraftId: number;
  type: ActionItemType;
  priority: number;
  description: string;
  status: ActionItemStatus;
  pendingAt?: number;
  verifyAt?: number;
  verificationError?: ActionItemVerificationError;
  completedAt?: number;
  archivedAt?: number;
  archiveReason?: ActionItemArchiveReason;
  emailDraft?: EmailDraft;
  emailDrafts?: EmailDraft[]; // Action Item have multiple email drafts
  outreachContact?: OutreachContact;
  subject?: string;

  // Only for FE
  brandName?: string;
};

export type AffiliateOrganization = {
  id: number;
  organizationId: number;
  isActive: boolean;
  stripeCouponId: string;
  stripePromotionCodeId: string;
  stripePromotionCode: string;
  referralLink: string;
};

export type CustomDomainInfo = {
  domain: string;
  available?: boolean;
  price?: number;
  username?: string;
  first_name?: string;
  last_name?: string;
};

export enum AdminCsvUploadRoute {
  CREATE_CONTACTS = "bento-contacts/create-from-csv",
  UPDATE_BRANDS = "bento-brands/update-from-csv",
  UPLOAD_CATEGORIES = "bento-categories/upload",
}

export enum AgentSessionType {
  template = "template",
  template_onboarding = "template_onboarding",
  outreach = "outreach",
  manage_inbox = "manage_inbox",
  analytics = "analytics",
}

export type AgentSession = {
  id: number;
  type: AgentSessionType;
  name: string;
};

export enum OrganizationNicheStatus {
  pending = "pending",
  active = "active",
  canceled = "canceled",
}

export type OrganizationNiche = {
  id?: number;
  name: string;
  notes: string;
  status?: OrganizationNicheStatus;
  emailSettingId?: number | null;
  emailSetting?: EmailSettings | null;
  bentoCategoryId?: number | null;
  templates?: Template[];

  countryCode?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  addressComponents?: AddressComponent[];
  isArchived?: boolean;
  followUpResponses?: NicheFollowUpResponse[];

  // Only use on the FE
  canEdit?: boolean;
  needsTemplate?: boolean;
};

export enum TemplateIntakeFormStatus {
  pending = "pending",
  completed = "completed",
}

export type TemplateIntakeForm = {
  status: TemplateIntakeFormStatus;
  notes?: string;
  pastBrands?: string;
  topPerformingPostLink?: string;
  partnershipReason?: string;
  nicheId?: number;
  niche?: OrganizationNiche;
  addOnItemId?: number;
  nicheName?: string;
};

export type FollowUpQuestionOption = {
  option: string;
};

export type NicheFollowUpQuestion = {
  id: number;
  question: string;
  description: string | null;
  options: FollowUpQuestionOption[];
  isRequired: boolean;
  triggerBentoCategoryId: number;
};

export type NicheFollowUpResponse = {
  organizationNicheId: number;
  nicheFollowUpQuestionId: number;
  selectedOptions: FollowUpQuestionOption[];
  // Only use on the FE
  followUpQuestion: NicheFollowUpQuestion;
};

export type TemplateError = {
  idx: number;
  message: string;
};

export enum TrackedCreatorStatus {
  pending = "pending",
  processed = "processed",
}

export type SocialMediaAccount = {
  username: string;
  name: string;
  description: string;
  accountCategory: string;
  businessCategory: string;
  numOfFollowers: number;
  numOfFollowing: number;
  numOfPosts: number;
  platformAccountId: string;
  profilePicUrl: string;
};

export type TrackedCreator = {
  id: number;
  socialMediaAccount: SocialMediaAccount;
  organizationId: number;
  autoTrackingEnabled: boolean;
  status: TrackedCreatorStatus;
  createdAt: number;
};

export type TrackedCreatorWrapper = {
  trackedCreator: TrackedCreator;
  id: number;
  unsentCount: number;
  sentCount: number;
  sourceInstagramUsername: string;
  status: string;
};
